import type { Dayjs } from 'dayjs'

import dayjs from 'utils/dayjs'

import { FileDelivery, JobSummary } from 'redux/selectors/internal/DataRuns'
import { deliveryKey, jobKey } from '../utils'

/** The three window presets from the design - "auto" is the default. */
export type TimelineWindowMode = 'auto' | 'evening' | 'day'

export interface TimelineWindow {
  end: Dayjs
  start: Dayjs
}

/** A single bar's worth of data, already resolved to local (Stockholm) time. */
export interface TimelineRun {
  durationMin: number | null
  end: Dayjs
  isRunning: boolean
  key: string
  start: Dayjs
  status: JobSummary['status']
}

/** Minimum timeline span, in minutes, for the "auto" window. */
const MIN_AUTO_SPAN_MIN = 120

/** Padding added around the customer's own runs before rounding to the grid. */
const AUTO_WINDOW_PADDING_MIN = 20

/** The 30-minute grid the "auto" window start/end are rounded to. */
const AUTO_WINDOW_STEP_MIN = 30

/**
 * How far back from the latest run's end another run can start and still
 * count as part of the same nightly batch. A batch spans roughly 21:00 to
 * 06:00, and the next one is a full 24h away, so anything in between
 * separates them safely - while still keeping an extra morning or lunch
 * run (the Glue-workflow customers) together with the night it follows.
 */
const LATEST_BATCH_LOOKBACK_HOURS = 18

/** Candidate tick intervals, smallest first - see `pickTickMinutes`. */
const TICK_INTERVAL_OPTIONS_MIN = [15, 30, 60, 120, 180, 240, 360, 720, 1440]

const MINUTES_PER_DAY = 1440

/** Minimum pixels a tick needs so two adjacent labels never collide. */
const MIN_PX_PER_TICK = 52

function toLocal(iso: string): Dayjs {
  return dayjs(iso).tz()
}

/** End of a finished run: start + duration, or its start when unknown. */
function endFromDuration(start: Dayjs, durationMin: number | null): Dayjs {
  return durationMin === null ? start : start.add(durationMin, 'minute')
}

/**
 * A job's runs to consider for the timeline: its own latest run plus any
 * `history` entries, deduped against the latest run by start time (the
 * newest history entry is normally the same run as the job's own fields).
 * Sorted ascending by start.
 */
export function getJobRuns(job: JobSummary, now: Dayjs): TimelineRun[] {
  const key = jobKey(job)

  const ownStart = toLocal(job.started_at)
  const ownIsRunning = job.status === 'running'

  const ownRun: TimelineRun = {
    durationMin: job.duration_min,
    end: job.completed_at
      ? toLocal(job.completed_at)
      : ownIsRunning
        ? now
        : endFromDuration(ownStart, job.duration_min),
    isRunning: ownIsRunning,
    key: `${key}-latest`,
    start: ownStart,
    status: job.status
  }

  // Only the latest run can still be running.
  const historyRuns = (job.history ?? [])
    .filter((entry) => entry.started_at !== job.started_at)
    .map((entry, index): TimelineRun => {
      const start = toLocal(entry.started_at)

      return {
        durationMin: entry.duration_min,
        end: endFromDuration(start, entry.duration_min),
        isRunning: false,
        key: `${key}-history-${index}`,
        start,
        status: entry.status
      }
    })

  return [ownRun, ...historyRuns].sort(
    (a, b) => a.start.valueOf() - b.start.valueOf()
  )
}

function floorToStep(t: Dayjs, stepMin: number): Dayjs {
  const totalMin = t.hour() * 60 + t.minute()

  return t
    .startOf('day')
    .add(Math.floor(totalMin / stepMin) * stepMin, 'minute')
}

function ceilToStep(t: Dayjs, stepMin: number): Dayjs {
  const totalMin = t.hour() * 60 + t.minute()

  return t.startOf('day').add(Math.ceil(totalMin / stepMin) * stepMin, 'minute')
}

function computeAutoWindow(runs: TimelineRun[], now: Dayjs): TimelineWindow {
  if (runs.length === 0) {
    const start = floorToStep(now, AUTO_WINDOW_STEP_MIN)

    return { end: start.add(MIN_AUTO_SPAN_MIN, 'minute'), start }
  }

  const minStart = runs.reduce(
    (min, run) => (run.start.isBefore(min) ? run.start : min),
    runs[0].start
  )
  const maxEnd = runs.reduce(
    (max, run) => (run.end.isAfter(max) ? run.end : max),
    runs[0].end
  )

  const start = floorToStep(
    minStart.subtract(AUTO_WINDOW_PADDING_MIN, 'minute'),
    AUTO_WINDOW_STEP_MIN
  )
  let end = ceilToStep(
    maxEnd.add(AUTO_WINDOW_PADDING_MIN, 'minute'),
    AUTO_WINDOW_STEP_MIN
  )

  if (end.diff(start, 'minute') < MIN_AUTO_SPAN_MIN) {
    end = start.add(MIN_AUTO_SPAN_MIN, 'minute')
  }

  return { end, start }
}

/**
 * The runs belonging to the most recent batch. Every mode needs this: the
 * job list carries up to 14 days of `history`, so measuring across all of
 * it would stretch the window over two weeks (and the header, which only
 * renders HH:mm, would hide that it had).
 */
export function getLatestBatchRuns(runs: TimelineRun[]): TimelineRun[] {
  if (runs.length === 0) {
    return runs
  }

  const latestEnd = runs.reduce(
    (max, run) => (run.end.isAfter(max) ? run.end : max),
    runs[0].end
  )
  const cutoff = latestEnd.subtract(LATEST_BATCH_LOOKBACK_HOURS, 'hour')

  return runs.filter((run) => !run.end.isBefore(cutoff))
}

/**
 * Reference calendar day for the fixed presets - the day the batch's
 * earliest run belongs to. A run before noon is treated as the tail end of
 * the previous evening's window rather than the start of a new one.
 */
function referenceDay(runs: TimelineRun[], now: Dayjs): Dayjs {
  const first =
    runs.length > 0
      ? runs.reduce(
        (min, run) => (run.start.isBefore(min) ? run.start : min),
        runs[0].start
      )
      : now

  return first.hour() < 12
    ? first.subtract(1, 'day').startOf('day')
    : first.startOf('day')
}

export function computeWindow(
  mode: TimelineWindowMode,
  runs: TimelineRun[],
  now: Dayjs
): TimelineWindow {
  const batch = getLatestBatchRuns(runs)

  if (mode === 'auto') {
    return computeAutoWindow(batch, now)
  }

  const day = referenceDay(batch, now)

  if (mode === 'evening') {
    return { end: day.add(1, 'day').add(8, 'hour'), start: day.add(18, 'hour') }
  }

  return { end: day.add(1, 'day').add(18, 'hour'), start: day.add(18, 'hour') }
}

/**
 * First of 15/30/60/120/180/240/360/720/1440 min where
 * `trackPx / (span/tick) >= 52`, so adjacent axis labels can never collide;
 * whole days beyond that. Null while the track hasn't been measured yet
 * (`trackPx` is 0) - there is no width to fit labels into.
 */
export function pickTickMinutes(
  trackPx: number,
  spanMin: number
): number | null {
  if (trackPx <= 0) {
    return null
  }

  if (spanMin <= 0) {
    return TICK_INTERVAL_OPTIONS_MIN[0]
  }

  const fitting = TICK_INTERVAL_OPTIONS_MIN.find(
    (tick) => trackPx / (spanMin / tick) >= MIN_PX_PER_TICK
  )

  if (fitting !== undefined) {
    return fitting
  }

  const minTickMin = (spanMin * MIN_PX_PER_TICK) / trackPx

  return Math.ceil(minTickMin / MINUTES_PER_DAY) * MINUTES_PER_DAY
}

export function buildTicks(window: TimelineWindow, tickMin: number): Dayjs[] {
  const ticks: Dayjs[] = []
  let cursor = window.start

  while (!cursor.isAfter(window.end)) {
    ticks.push(cursor)
    cursor = cursor.add(tickMin, 'minute')
  }

  return ticks
}

/** Clamped 0-100 position of `t` within `window`, as a percentage. */
export function percentInWindow(t: Dayjs, window: TimelineWindow): number {
  const spanMs = window.end.diff(window.start)

  if (spanMs <= 0) {
    return 0
  }

  const pct = (t.diff(window.start) / spanMs) * 100

  return Math.min(100, Math.max(0, pct))
}

export function runIntersectsWindow(
  run: TimelineRun,
  window: TimelineWindow
): boolean {
  return run.start.isBefore(window.end) && !run.end.isBefore(window.start)
}

/* ------------------------------------------------------------------ *
 * File deliveries on the timeline
 *
 * A delivery is an instant, not a span: it gets a marker rather than a
 * bar, and its lane never influences the window (see placeDeliveryMarkers).
 * ------------------------------------------------------------------ */

export interface TimelineMarker {
  at: Dayjs
  key: string
  status: 'ok' | 'fail'
}

export interface PlacedDeliveryMarkers {
  /** Markers that fall inside the window, drawn at their own position. */
  inside: TimelineMarker[]
  /**
   * The most recent delivery *before* the window, if any. Pinned to the
   * left edge rather than dropped: a lane rendered empty reads as "this
   * customer never delivered", which for a file that arrived the previous
   * morning is simply false.
   */
  pinnedBefore: TimelineMarker | null
  /** Mirror of the above past the right edge. Rare, but possible. */
  pinnedAfter: TimelineMarker | null
}

/**
 * Every recorded arrival for one source, newest first.
 *
 * Uses `history` when the endpoint sent it (customer scope, 14 days) and
 * falls back to the single latest delivery otherwise, so a lane always has
 * something to say.
 */
export function getDeliveryMarkers(delivery: FileDelivery): TimelineMarker[] {
  const key = deliveryKey(delivery)

  if (delivery.history && delivery.history.length > 0) {
    return delivery.history.map((entry, index) => ({
      at: toLocal(entry.delivered_at),
      key: `${key}-history-${index}`,
      status: entry.status
    }))
  }

  if (!delivery.last_delivery_at) {
    return []
  }

  return [
    {
      at: toLocal(delivery.last_delivery_at),
      key: `${key}-latest`,
      status: delivery.status === 'fail' ? 'fail' : 'ok'
    }
  ]
}

/**
 * Splits a source's markers into the ones the window can show and the
 * nearest one on either side of it.
 *
 * Only the nearest outside marker is kept: a 14-day history against a
 * window of hours would otherwise stack a dozen markers on the same edge
 * pixel, all saying the same thing.
 */
export function placeDeliveryMarkers(
  markers: TimelineMarker[],
  window: TimelineWindow
): PlacedDeliveryMarkers {
  const inside: TimelineMarker[] = []
  let pinnedBefore: TimelineMarker | null = null
  let pinnedAfter: TimelineMarker | null = null

  markers.forEach((marker) => {
    if (marker.at.isBefore(window.start)) {
      if (!pinnedBefore || marker.at.isAfter(pinnedBefore.at)) {
        pinnedBefore = marker
      }

      return
    }

    if (marker.at.isAfter(window.end)) {
      if (!pinnedAfter || marker.at.isBefore(pinnedAfter.at)) {
        pinnedAfter = marker
      }

      return
    }

    inside.push(marker)
  })

  return { inside, pinnedBefore, pinnedAfter }
}
